/**
 * WeightModal — POS weight entry for weight-based products.
 *
 * Two sections:
 *   TOP:    Scale mode — read-only display for future Electron live scale input
 *   BOTTOM: Manual mode — editable weight entry for browser POS
 *
 * Desktop-ready: accepts optional `scaleWeight` prop. When provided and stable,
 * the top section shows the live weight and auto-confirms.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, Box, Typography, TextField, Button,
  IconButton, InputAdornment, Divider, alpha, CircularProgress, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScaleIcon from "@mui/icons-material/Scale";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";

type ScaleStatus = "waiting" | "reading" | "stable" | "disconnected";

interface WeightModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (weight: number) => void;
  productName: string;
  pricePerUnit: number;
  unitAbbreviation?: string;
  /** Future: live weight from Electron IPC / scale hardware */
  scaleWeight?: number;
  /** Future: scale connection status */
  scaleStatus?: ScaleStatus;
}

const MIN_WEIGHT = 0.010;

const statusConfig: Record<ScaleStatus, { label: string; color: string; bg: string }> = {
  waiting:      { label: "Waiting for scale...", color: "#94a3b8", bg: "#f1f5f9" },
  reading:      { label: "Reading...",           color: "#f59e0b", bg: "#fffbeb" },
  stable:       { label: "Weight stable",        color: "#059669", bg: "#f0fdf4" },
  disconnected: { label: "Scale not connected",  color: "#ef4444", bg: "#fef2f2" },
};

const WeightModal: React.FC<WeightModalProps> = ({
  open, onClose, onConfirm, productName, pricePerUnit,
  unitAbbreviation = "kg", scaleWeight, scaleStatus,
}) => {
  const [manualWeight, setManualWeight] = useState("");
  const [error, setError] = useState("");

  // Coerce price to number (API may return string)
  const safePricePerUnit = Number(pricePerUnit) || 0;

  // Determine if we have a live scale connected (future desktop mode)
  const hasScale = scaleWeight !== undefined && scaleStatus !== "disconnected";
  const effectiveStatus = scaleStatus || "disconnected";

  // The active weight — from scale if connected, otherwise from manual input
  const numWeight = hasScale && scaleWeight ? scaleWeight : (parseFloat(manualWeight) || 0);
  const subtotal = numWeight * safePricePerUnit;
  const isValid = numWeight >= MIN_WEIGHT;

  // Reset on open
  useEffect(() => {
    if (open) { setManualWeight(""); setError(""); }
  }, [open]);

  // Desktop only: auto-confirm when hardware scale reports stable weight.
  // This NEVER fires in browser manual mode because scaleStatus is undefined.
  useEffect(() => {
    if (!scaleStatus || scaleStatus !== "stable") return;       // must be hardware-reported stable
    if (!scaleWeight || scaleWeight < MIN_WEIGHT) return;        // must have valid weight
    const timer = setTimeout(() => onConfirm(scaleWeight), 1000);
    return () => clearTimeout(timer);
  }, [scaleStatus, scaleWeight]);

  const handleConfirm = useCallback(() => {
    if (numWeight < MIN_WEIGHT) {
      setError(`Minimum weight is ${MIN_WEIGHT} ${unitAbbreviation}`);
      return;
    }
    onConfirm(numWeight);
  }, [numWeight, unitAbbreviation, onConfirm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) handleConfirm();
    if (e.key === "Escape") onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      {/* ═══ HEADER ═══ */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.1)",
          }}>
            <ScaleIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Box>
            <Typography fontSize={16} fontWeight={700} color="#fff">Place item on scale</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.5)" sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {productName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip label={`$${safePricePerUnit.toFixed(2)}/${unitAbbreviation}`} size="small"
            sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }} />
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>

        {/* ═══ TOP SECTION: SCALE MODE ═══ */}
        <Box sx={{
          p: 3, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FitnessCenterIcon sx={{ fontSize: 16, color: "#64748b" }} />
            <Typography fontSize={12} fontWeight={600} color="#64748b" textTransform="uppercase" letterSpacing="0.04em">
              Scale Reading
            </Typography>
          </Box>

          {/* Large weight display (read-only — driven by scale or shows manual input) */}
          <Box sx={{
            display: "flex", alignItems: "baseline", justifyContent: "center",
            p: 2.5, borderRadius: 2.5, bgcolor: "#fff", border: "1px solid #e2e8f0",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
          }}>
            <Typography sx={{
              fontSize: 48, fontWeight: 800, fontFamily: "monospace", color: isValid ? "#0f172a" : "#cbd5e1",
              letterSpacing: "-0.02em", lineHeight: 1,
            }}>
              {numWeight > 0 ? numWeight.toFixed(3) : "0.000"}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#94a3b8", ml: 1 }}>
              {unitAbbreviation}
            </Typography>
          </Box>

          {/* Scale status indicator */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            mt: 1.5, p: 1, borderRadius: 1.5,
            bgcolor: statusConfig[effectiveStatus].bg,
          }}>
            {effectiveStatus === "reading" && <CircularProgress size={12} sx={{ color: statusConfig.reading.color }} />}
            {effectiveStatus === "stable" && <CheckCircleIcon sx={{ fontSize: 14, color: statusConfig.stable.color }} />}
            <Typography fontSize={11} fontWeight={600} color={statusConfig[effectiveStatus].color}>
              {hasScale ? statusConfig[effectiveStatus].label : "Manual entry mode"}
            </Typography>
          </Box>
        </Box>

        {/* ═══ BOTTOM SECTION: MANUAL MODE ═══ */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <EditIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography fontSize={12} fontWeight={600} color="#64748b" textTransform="uppercase" letterSpacing="0.04em">
              Or enter weight manually
            </Typography>
          </Box>

          <TextField
            fullWidth autoFocus type="number" placeholder="0.000"
            value={manualWeight}
            onChange={(e) => { setManualWeight(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown}
            error={!!error}
            helperText={error}
            inputProps={{ step: "0.001", min: "0" }}
            InputProps={{
              sx: { fontSize: 22, fontWeight: 700, fontFamily: "monospace", borderRadius: 2 },
              endAdornment: (
                <InputAdornment position="end">
                  <Typography fontSize={14} fontWeight={600} color="#94a3b8">{unitAbbreviation}</Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#e2e8f0" },
                "&.Mui-focused fieldset": { borderColor: "#0f172a", borderWidth: 2 },
              },
            }}
          />

          {/* Live calculated total */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            mt: 2.5, p: 2, borderRadius: 2,
            bgcolor: isValid ? alpha("#059669", 0.04) : "#f8fafc",
            border: `1px solid ${isValid ? alpha("#059669", 0.15) : "#e2e8f0"}`,
            transition: "all 0.2s",
          }}>
            <Box>
              <Typography fontSize={11} color="#94a3b8" fontWeight={500}>Subtotal</Typography>
              {isValid && (
                <Typography fontSize={11} color="#64748b" mt={0.25}>
                  {numWeight.toFixed(3)} {unitAbbreviation} × ${safePricePerUnit.toFixed(2)}
                </Typography>
              )}
            </Box>
            <Typography fontSize={26} fontWeight={800} color={isValid ? "#059669" : "#cbd5e1"} sx={{ fontFamily: "monospace" }}>
              ${isValid ? subtotal.toFixed(2) : "0.00"}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
            <Button fullWidth variant="outlined" onClick={onClose}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, py: 1.25, borderColor: "#e2e8f0", color: "#64748b" }}>
              Cancel
            </Button>
            <Button fullWidth variant="contained" disableElevation disabled={!isValid} onClick={handleConfirm}
              startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, py: 1.25,
                bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" },
                "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
              }}>
              Confirm Weight
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WeightModal;
